import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { getScoringListing } from '../../services/userservice';
import { 
  canAccessAnalisisCrediticio,
  canAccessDetalleCaso,
  canAccessControlCasos,
  canAccessBusquedaCasos,
  canAccessTrackingEventos
} from '../../utils/auth';
const ScoringDashboard = ({ user, onLogout, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [casosStats, setCasosStats] = useState({
    incompletos: 0,
    rechazados: 0,
    enRevision: 0
  });
  // Todos los módulos disponibles con sus permisos por grupo
  // CONCESIONARIO: Análisis Disponible, Control de Casos, Búsqueda, Tracking
  // HONDA: Detalle del Caso, Búsqueda, Tracking
  // XEROX: Todo
  const allModules = [
    { 
      id: 1, 
      title: 'ANÁLISIS CREDITICIOS DISPONIBLES', 
      icon: 'analytics', 
      description: 'Revisar y evaluar solicitudes de análisis crediticio pendientes', 
      action: 'analisisCrediticioDisponible',
      canAccess: canAccessAnalisisCrediticio
    },
    { 
      id: 2, 
      title: 'ANÁLISIS DE CASOS', 
      icon: 'description', 
      description: 'Consultá, validá y gestioná toda la información del caso.', 
      action: 'analisisDeCasos',
      canAccess: canAccessDetalleCaso
    },
    { 
      id: 3, 
      title: 'CONTROL DE CASOS', 
      icon: 'assignment', 
      description: 'Consulta y gestiona casos incompletos, rechazados y en revisión.', 
      action: 'casos',
      badge: null,
      canAccess: canAccessControlCasos
    },
    { 
      id: 4, 
      title: 'BÚSQUEDA DE CASOS', 
      icon: 'search', 
      description: 'Localizar y consultar casos específicos por diferentes criterios', 
      action: 'busquedaCasos',
      canAccess: canAccessBusquedaCasos
    },
    { 
      id: 5, 
      title: 'TRACKING DE EVENTOS', 
      icon: 'history', 
      description: 'Registro y seguimiento de todas las actividades del sistema', 
      action: 'trackingEventos',
      canAccess: canAccessTrackingEventos
    }
  ];
  // Función para filtrar módulos según permisos de grupo
  const getFilteredModules = () => {
    // Filtrar módulos según las funciones de acceso de cada uno
    return allModules.filter(module => {
      // Verificar si el usuario tiene acceso según su grupo
      return module.canAccess && module.canAccess();
    });
  };
  const [modules, setModules] = useState(getFilteredModules());
  // Cargar estadísticas reales de casos al montar el componente
  useEffect(() => {
    const loadCasosStats = async () => {
      try {
        setLoading(true);
        // Obtener casos incompletos, rechazados y en revisión del servicio
        const [incompletos, rechazados, enRevision] = await Promise.all([
          getScoringListing('INCOMPLETOS', user.ce_ID),
          getScoringListing('RECHAZADOS', user.ce_ID),
          getScoringListing('EN REVISION', user.ce_ID)
        ]);
        const stats = {
          incompletos: incompletos.length,
          rechazados: rechazados.length,
          enRevision: enRevision.length
        };
        setCasosStats(stats);
        // Actualizar el badge del módulo de Control de Casos (solo rechazados e incompletos)
        setModules(prevModules => 
          prevModules.map(module => 
            module.id === 3 
              ? { 
                  ...module, 
                  badge: {
                    rechazados: stats.rechazados,
                    incompletos: stats.incompletos
                  }
                } 
              : module
          )
        );
      } catch (err) {
        console.error('Error cargando estadísticas de casos:', err);
        // En caso de error, mostrar 0
        setCasosStats({ incompletos: 0, rechazados: 0, enRevision: 0 });
      } finally {
        setLoading(false);
      }
    };
    loadCasosStats();
  }, []);
  // Función para obtener clases de grid según cantidad de módulos
  // Siempre centrado, adaptándose a la cantidad de módulos
  const getGridClasses = () => {
    const moduleCount = modules.length;
    if (moduleCount === 1) {
      // 1 módulo: centrado, ancho máximo de 1 card
      return "grid grid-cols-1 gap-6 md:gap-8 max-w-md mx-auto";
    } else if (moduleCount === 2) {
      // 2 módulos: centrados, máximo 2 columnas
      return "grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-3xl mx-auto";
    } else if (moduleCount === 3) {
      // 3 módulos: centrados, máximo 3 columnas
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10 max-w-6xl mx-auto";
    } else if (moduleCount === 4) {
      // 4 módulos: centrados, máximo 4 columnas
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto";
    } else if (moduleCount === 5) {
      // 5 módulos: distribuidos en grid responsivo
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-8 max-w-7xl mx-auto";
    } else {
      // 6+ módulos: grid responsivo completo
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto";
    }
  };
  const handleModuleClick = (module) => {
    if (module.action) {
      onNavigate(module.action);
    } else {
      alert(`Funcionalidad "${module.title}" en desarrollo`);
    }
  };
  // Determinar el subtítulo según los permisos del usuario
  const getSubtitle = () => {
    // Subtítulo genérico que aplica a todos los usuarios con acceso a SCORING
    return 'Gestión integral de análisis y evaluación crediticia';
  };
  // Mostrar loader mientras cargan los datos
  if (loading) {
    return (
      <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
        <div className="flex items-center justify-center h-screen">
          <HondaLoader />
        </div>
      </MainLayout>
    );
  }
  return (
    <MainLayout 
      user={user} 
      onLogout={onLogout} 
      onNavigate={onNavigate}
    >
      <div className="px-3 xl:px-4 py-4 xl:py-6">
        <div className="max-w-full mx-auto w-full">
          <div className="mb-5 xl:mb-8 text-center">
            <h2 className="text-xl md:text-2xl xl:text-3xl font-bold text-gradient uppercase tracking-wide flex items-center justify-center gap-2">
              <div className="w-1 h-6 xl:h-8 bg-red-600 rounded-full"></div>
              SCORING CREDITICIO
              <div className="w-1 h-6 xl:h-8 bg-red-600 rounded-full"></div>
            </h2>
            <p className="text-gray-500 mt-1 xl:mt-2 text-xs xl:text-sm md:text-base">{getSubtitle()}</p>          
          </div>
          <div className={getGridClasses()}>
          {modules.map((module) => (
            <div key={module.id} className="relative group cursor-pointer transition-transform duration-300 hover:scale-105 hover:z-10">
              {/* Card principal */}
              <div
                onClick={() => handleModuleClick(module)}
                className="h-full min-h-[220px] md:min-h-[260px] xl:min-h-[300px]"
              >
                <div className="bg-white border border-gray-200 rounded-xl shadow-md group-hover:shadow-xl transition-all duration-300 group-hover:border-red-300 overflow-hidden h-full flex flex-col">
                  <div className="h-1.5 xl:h-2 bg-red-600 group-hover:bg-red-700 transition-colors flex-shrink-0"></div>
                  <div className="p-4 md:p-6 xl:p-8 text-center flex-1 flex flex-col justify-center">
                    <div className="mb-4 xl:mb-6 flex justify-center flex-shrink-0">
                      <div className="w-16 h-16 md:w-20 md:h-20 xl:w-24 xl:h-24 bg-red-50 rounded-full flex items-center justify-center group-hover:bg-red-100 transition-colors shadow-sm">
                        <span className="material-icons text-3xl md:text-4xl xl:text-5xl text-red-600">
                          {module.icon}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-base md:text-lg xl:text-xl font-bold text-gray-800 mb-2 xl:mb-3 group-hover:text-red-700 transition-colors flex-shrink-0">
                      {module.title}
                    </h3>
                    {module.description && (
                      <p className="text-xs md:text-sm xl:text-base text-gray-500 leading-relaxed flex-1">
                        {module.description}
                      </p>
                    )}
                    <div className="mt-3 xl:mt-5 pt-2 xl:pt-3 invisible group-hover:visible transition-all flex-shrink-0">
                      <span className="text-red-600 text-xs md:text-sm xl:text-base font-medium flex items-center justify-center gap-1">
                        Acceder <span className="material-icons text-base md:text-lg">arrow_forward</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Badges estilo señaladores de libros */}
              {module.badge && (
                <div className="absolute right-2 flex items-start gap-2 z-10" style={{ top: '2px' }}>
                  {/* Señalador de Rechazados */}
                  <div className="relative group/rechazado">
                    {/* Tooltip */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 invisible group-hover/rechazado:opacity-100 group-hover/rechazado:visible transition-all duration-300 whitespace-nowrap pointer-events-none">
                      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white text-xs font-semibold rounded-lg px-3 py-2 shadow-xl">
                        <span className="flex items-center gap-1.5">
                          <span className="material-icons text-sm">cancel</span>
                          Rechazados: {module.badge.rechazados}
                        </span>
                      </div>
                    </div>
                    {/* Señalador */}
                    <div className="cursor-help transition-all duration-300 ease-in-out hover:-translate-y-1">
                      <div className="bg-gradient-to-b from-red-600 to-red-700 shadow-lg flex items-center justify-center"
                           style={{
                             width: '38px',
                             height: '55px',
                             clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
                             borderTopLeftRadius: '4px',
                             borderTopRightRadius: '4px',
                             paddingTop: '12px'
                           }}>
                        <span className="text-base font-bold text-white leading-none">
                          {module.badge.rechazados}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Señalador de Incompletos */}
                  <div className="relative group/incompleto">
                    {/* Tooltip */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 invisible group-hover/incompleto:opacity-100 group-hover/incompleto:visible transition-all duration-300 whitespace-nowrap pointer-events-none">
                      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-xs font-semibold rounded-lg px-3 py-2 shadow-xl">
                        <span className="flex items-center gap-1.5">
                          <span className="material-icons text-sm">pending</span>
                          Incompletos: {module.badge.incompletos}
                        </span>
                      </div>
                    </div>
                    {/* Señalador */}
                    <div className="cursor-help transition-all duration-300 ease-in-out hover:-translate-y-1">
                      <div className="bg-gradient-to-b from-yellow-500 to-yellow-600 shadow-lg flex items-center justify-center"
                           style={{
                             width: '38px',
                             height: '55px',
                             clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
                             borderTopLeftRadius: '4px',
                             borderTopRightRadius: '4px',
                             paddingTop: '12px'
                           }}>
                        <span className="text-base font-bold text-white leading-none">
                          {module.badge.incompletos}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          </div>
        </div>   
      </div>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .group-hover\\:opacity-100 {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </MainLayout>
  );
};
export default ScoringDashboard;

